<script>
   /*********************************************************
   * ScalarsPlot                                            *
   * --------------------                                   *
   * shows plot for c_k/c scalars from PCV methods          *
   **********************************************************/

   import { Vector, vector } from 'mdatools/arrays';
   import { mrange } from 'mdatools/stat';

   import { Axes, XAxis, YAxis, Heatmap, ColormapLegend, Rectangles, TextLabels, Segments, Box } from 'svelte-plots-basic/2d';
   import BoxplotSeries from './BoxplotSeries.svelte';

   /*****************************************/
   /* Input parameters                      */
   /*****************************************/

   export let scalars;
   export let params;
   export let pvoptions = null;

   function getTicks(n, max) {
      const v = Vector.seq(1, n);
      const vFactor = Math.ceil(n / max);
      return n <= 20 ? v : v.filter(val => val % vFactor == 0);
   }

   const title = 'Scalars';
   const margins = [0.75, 1.0, 0.1, 0.5];
   const xLabel = 'LV, a';

   const breaks = [-99999, -2, -1, 0, 0.2, 0.5, 0.8, 1.2, 1.5, 1.8, 2.0, 2.5, 3.0, 99999];
   const colmap = [
      '#6200DF', // < -2
      '#6923FF', // -2.00 to -1.00
      '#648FFF', // -1.00 to  0.00
      '#8CCDFD', //  0.00 to  0.25
      '#D2ECFF', //  0.25 to  0.50
      '#E8F5FF', //  0.50 to  0.75
      '#fefeff', //  0.75 to  1.25
      '#FFF5E5', //  1.25 to  1.50
      '#FFE5BE', //  1.50 to  1.75
      '#FFCE85', //  1.75 to  2.00
      '#FFA34E', //  2.00 to  2.50
      '#FF8024', //  2.50 to  3.00
      '#CF5903'  //  > 3.0
   ];

   // compute axis labels
   $: yLabel = params && params.plotType === 'hm' ? 'Segment, k' : 'Scalars, c<sub>k</sub>/c';

   // compute labels for colormap
   const clLabels = breaks.map(v => v.toFixed(1));
   clLabels[0] = '&minus;&infin;';
   clLabels[clLabels.length - 1] = '&infin;';

   // compute factors for axis ticks
   $: xTicks = scalars.ncols > 1 ? getTicks(scalars.ncols, 20) : vector([0.5, 1.0, 1.5]);
   $: yTicks = getTicks(scalars.nrows, 10);

   // compute limits
   $: yMin = 0.5  - 0.10 * scalars.nrows;
   $: yMax = scalars.nrows + 0.5 + 0.10 * scalars.nrows;
   $: limX = scalars.ncols > 10 ? [0.0, scalars.ncols + 1.0] : [0.25, scalars.ncols + 0.75];
   $: limY = params && params.plotType === 'hm' ? [yMin, yMax] : mrange(scalars.v, 0.1);

   // set number of components and color for selection
   $: ncomp = pvoptions ? pvoptions.ncompSel : null;
   $: color = pvoptions && ncomp > pvoptions.ncompRec ? 'red' : 'green';
</script>

{#if scalars && params}
   {#if params.plotType === 'hm'}
   <Axes {margins} {xLabel} {yLabel} {title} {limX} {limY} >
      <Heatmap values={scalars} breaks={vector(breaks)} {colmap} />
      {#if ncomp}
      <Rectangles left={[0.5]} top={[scalars.nrows + 0.5]} width={[ncomp]} height={[scalars.nrows]}
         faceColor="transparent" lineWidth={2} borderColor={color} />
      <TextLabels xValues={[ncomp]} yValues={[0.5]} labels={[ncomp]} pos={1} faceColor={color} />
      {/if}
      <ColormapLegend breaks={vector(breaks)} labels={clLabels} {colmap} fontSize={0.95} />
      <XAxis slot="xaxis" ticks={xTicks} />
      <YAxis slot="yaxis" ticks={yTicks} />
      <Box slot="box" />
   </Axes>
   {:else}
   <Axes {margins} {xLabel} {yLabel} {title} {limX} limY={[Math.min(-0.1, limY[0]), Math.max(2.0, limY[1])]} >
      <Segments xStart={[limX[0]]} yStart={[1]} xEnd={[limX[1]]} yEnd={[1]} lineColor="#c0c0c0" />
      <BoxplotSeries values={scalars} />
      <XAxis slot="xaxis" showGrid={true} ticks={xTicks} />
      <YAxis slot="yaxis" showGrid={true} />
      <Box slot="box" />
   </Axes>
   {/if}
{/if}


